import React, { useState, useEffect, useRef } from 'react' 
import { Row, Col, Button, Container, Spinner } from 'react-bootstrap'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { locationAPI } from '../api/locationAPI';
import ResizableTable from '../components/ResizableTable'
import { highlightText } from '../utils/commonUtils'
import { handleSearchWithDebounce } from '../utils/tableUtils'
import PaginationComponent from '../components/Pagination'
import SearchInputWithClear from '../components/SearchInputWithClear'

function LocationsListScreen() {
    const { warehouseId } = useParams()
    const { state } = useLocation()
    const warehouseName = state?.warehouseName || 'Unknown Warehouse'
    const [locations, setLocations] = useState([])
    const [showDeactivated, setShowDeactivated] = useState(false)
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(0)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())
    const debounceTimeoutRef = useRef(null)
    const navigate = useNavigate()

    const [columnWidths, setColumnWidths] = useState(() => {
        const savedWidths = localStorage.getItem('locationColumnWidths');
        return savedWidths ? JSON.parse(savedWidths) : {
            name: 'auto',
            description: 'auto',
            actions: 100
        };
    });

    useEffect(() => {
        localStorage.setItem('locationColumnWidths', JSON.stringify(columnWidths));
    }, [columnWidths]);

    useEffect(() => {
        async function fetchLocations() {
            try {
                setLoading(true)
                const response = await locationAPI.getAll(warehouseId, currentPage, pageSize, searchTerm, showDeactivated);
                setLocations(response.results)
                setTotalPages(response.count)
            } catch (error) {
                console.error('Error fetching locations:', error);
            } finally {
                setLoading(false)
            }
        }
        fetchLocations()
    }, [warehouseId, currentPage, pageSize, searchTerm, showDeactivated])

    const handleRowClick = (locationId) => {
        navigate(`/location/${locationId}`, { state: { warehouseId } })
    }

    const handleClearSearch = () => {
        setSearchInputValue('');
        setSearchTerm('');
        setCurrentPage(1);
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInputValue(value);
        handleSearchWithDebounce(
            value,
            setSearchTerm,
            setCurrentPage,
            debounceTimeoutRef
        );
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleToggleActive = async (e, location) => {
        e.stopPropagation();
        
        if (location.is_active) {
            if (!window.confirm('Are you sure you want to deactivate this location?')) {
                return;
            }
        }

        try {
            const updatedLocation = {
                ...location,
                is_active: !location.is_active
            };
            
            await locationAPI.update(location._id, updatedLocation);
            
            setLocations(locations.map(l => 
                l._id === location._id 
                    ? updatedLocation
                    : l
            ));
        } catch (error) {
            console.error('Error toggling location status:', error);
            alert(`Failed to update location: ${error.response?.data?.message || error.message}`);
        }
    };

    return (
        <Container className="py-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1>{warehouseName} Locations</h1>
                </Col>
                <Col md={4}>
                    <SearchInputWithClear
                        value={searchInputValue}
                        onChange={handleSearchChange}
                        onClear={handleClearSearch}
                        placeholder="Search locations..."
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Col>
                <Col xs="auto">
                    <Button 
                        variant="outline-secondary"
                        onClick={() => {
                            setShowDeactivated(!showDeactivated);
                            setCurrentPage(1);
                        }}
                        className="me-2"
                    >
                        {showDeactivated ? 'Hide Deactivated' : 'Show Deactivated'}
                    </Button>
                    <Button 
                        onClick={() => navigate('/location/create', {
                            state: {
                                warehouseId,
                                warehouseName
                            }
                        })} 
                        variant="success"
                    >
                        Create Location
                    </Button>
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            ) : (
                <>
                    <ResizableTable
                        tableId="locations_table"
                        columns={[
                            {
                                id: 'name',
                                header: 'Name',
                                cell: (row) => (
                                    <>
                                        {highlightText(row.name, searchTerm)}
                                        {!row.is_active && (
                                            <span className="ms-2 text-muted fst-italic">
                                                (Inactive)
                                            </span>
                                        )}
                                    </>
                                )
                            },
                            {
                                id: 'description',
                                header: 'Description',
                                cell: (row) => highlightText(row.description, searchTerm)
                            },
                            {
                                id: 'actions',
                                header: 'Actions',
                                cell: (row) => (
                                    <Button 
                                        variant={row.is_active ? "danger" : "success"}
                                        size="sm"
                                        onClick={(e) => handleToggleActive(e, row)}
                                    >
                                        {row.is_active ? 'Deactivate' : 'Activate'}
                                    </Button>
                                )
                            }
                        ]}
                        data={locations}
                        initialColumnWidths={columnWidths}
                        onRowClick={(row) => handleRowClick(row._id)}
                        striped
                        hover
                    />
                    <PaginationComponent
                        currentPage={currentPage}
                        totalItems={totalPages}
                        onPageChange={handlePageChange}
                        onItemsPerPageChange={setPageSize}
                    />
                </>
            )}
        </Container>
    )
}

export default LocationsListScreen
